import { formatDateTime } from '../../utils/datetime';
import { FUNNEL_CARD_KEYS, FUNNEL_FILTER_COPY } from './reports.constants';

/**
 * Pure helpers behind the clickable KPI cards (KAN-583 / KAN-616). Kept free
 * of React and of the controller so the behaviour (toggle, URL round-trip,
 * card-vs-table check) is unit-testable without mounting the Reports screen.
 *
 * Contract (clockout-service KAN-615): the cards are an all-time snapshot —
 * `range: { type: 'all_time', generatedAt }` — and each card carries a
 * per-caller `filterable` flag (First Advance is off for callers without
 * `admin:roles:manage`). `GET /api/users/fi/:aba?funnelStage=<card key>`
 * returns exactly the population the card counted and echoes `funnel_stage`
 * and `funnel_range` (the same all-time shape) in its envelope.
 */

/**
 * Can this card filter the Users table? Decided by the payload, never
 * locally: the service computes `filterable` per caller, and a legacy
 * payload without the flag renders static tiles (`normalizeFunnelPayload`
 * reads an absent flag as `false`).
 *
 * @param {{ filterable?: boolean } | null | undefined} card normalized card
 * @returns {boolean}
 */
export function isFilterableFunnelCard(card) {
    return card?.filterable === true;
}

/**
 * Accept a card key coming from the URL only if it is a real card key;
 * anything else (typo, retired `active`, a stage value that belongs to the
 * journey-stage chip) is dropped so the table never asks the service for a
 * filter it will refuse with 400. Whether THIS caller may filter by the card
 * is settled once the cards load — see `ReportsController.reconcileFunnelStage`.
 *
 * @param {string|null|undefined} raw
 * @returns {string|null}
 */
export function readFunnelStageParam(raw) {
    if (typeof raw !== 'string') return null;
    const value = raw.trim();
    return FUNNEL_CARD_KEYS.includes(value) ? value : null;
}

/**
 * Replace, don't stack: clicking the active card clears it, clicking another
 * card swaps to it.
 *
 * @param {string|null} current
 * @param {string} next
 * @returns {string|null}
 */
export function toggleFunnelStage(current, next) {
    return current === next ? null : next;
}

/**
 * Human label for a card key (chip text), from the server funnel when loaded.
 * The server is the only source of card labels (`funnelPayload.js`), so
 * before it has answered the key itself is shown rather than an invented name.
 *
 * @param {string|null} cardKey
 * @param {Array<{key:string,label:string}>|null} [funnel]
 * @returns {string|null}
 */
export function funnelStageLabel(cardKey, funnel) {
    if (!cardKey) return null;
    const fromServer = Array.isArray(funnel) ? funnel.find((c) => c.key === cardKey) : null;
    return fromServer?.label || cardKey;
}

/** `"Card: Payroll link error"` for the active-filters chip. */
export function funnelStageChipText(cardKey, funnel) {
    const label = funnelStageLabel(cardKey, funnel);
    return label ? `${FUNNEL_FILTER_COPY.CHIP_PREFIX}: ${label}` : null;
}

/**
 * Two ranges describe the same window? The cards and the card population
 * are both all-time snapshots, so two all-time ranges are one window whatever
 * their `generatedAt` says (the snapshot cache refreshes between the two
 * requests). `null` when either side is unknown — nothing to compare.
 *
 * @param {{ type?: string, start?: string, end?: string } | null | undefined} a
 * @param {{ type?: string, start?: string, end?: string } | null | undefined} b
 * @returns {boolean|null}
 */
export function sameFunnelRange(a, b) {
    if (!a?.type || !b?.type) return null;
    if (a.type !== b.type) return false;
    if (a.type === 'all_time') return true;
    return (a.start ?? null) === (b.start ?? null) && (a.end ?? null) === (b.end ?? null);
}

/**
 * One-line description of a range for the mismatch warning: "all time
 * (snapshot 09/14/2026 10:05 AM)" or the window's days.
 *
 * @param {{ type?: string, generatedAt?: string|null, start?: string, end?: string } | null | undefined} range
 * @returns {string}
 */
export function describeFunnelRange(range) {
    if (!range?.type) return FUNNEL_FILTER_COPY.RANGE_UNAVAILABLE;
    if (range.type === 'all_time') {
        const at = range.generatedAt ? formatDateTime(range.generatedAt) : null;
        return at ? `all time (snapshot ${at})` : 'all time';
    }
    if (range.start && range.end) return `${range.start} – ${range.end}`;
    return range.type;
}

/**
 * KAN-583: when the table's total differs from the card's count, surface it —
 * never adjust either number. Returns null when there is nothing to report
 * (no active card, funnel not loaded, or they agree).
 *
 * When both the cards' `range` and the users envelope's `funnel_range` are
 * known they are compared too: `rangeDiffers` tells the reader the two
 * numbers were computed over different windows (the drift is in the window,
 * not the formula).
 *
 * @param {Array<{key:string,count:number|null}>|null} funnel
 * @param {string|null} cardKey
 * @param {number|null|undefined} total  users table `pageinfo.count`
 * @param {{ cardRange?: object|null, tableRange?: object|null }} [ranges]
 * @returns {{ stage: string, cardCount: number, tableCount: number, cardRange: object|null, tableRange: object|null, rangeDiffers: boolean } | null}
 */
export function detectFunnelMismatch(funnel, cardKey, total, { cardRange = null, tableRange = null } = {}) {
    if (!cardKey || !Array.isArray(funnel)) return null;
    const card = funnel.find((c) => c.key === cardKey);
    if (!card || card.count == null || total == null) return null;
    const cardCount = Number(card.count);
    const tableCount = Number(total);
    if (!Number.isFinite(cardCount) || !Number.isFinite(tableCount) || cardCount === tableCount) return null;
    return {
        stage: cardKey,
        cardCount,
        tableCount,
        cardRange: cardRange || null,
        tableRange: tableRange || null,
        rangeDiffers: sameFunnelRange(cardRange, tableRange) === false,
    };
}
